import { useCallback } from 'react';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Flex from '@creditflowresearch/uilib/Flex';
import {
  BondScreenerApplySaveButton,
} from '@/creditflowresearch/ui/common/components/molecules/BondScreenerApplySaveButton';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import {
  useColumnsCustomizationContext,
} from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomizationContext';
import {
  useBondScreenerViewsContext,
} from '@/creditflowresearch/application/BondScreener/Sidebar/BondScreenerViewsContext';
import {
  REQUIRED_FILTER_LABELS,
} from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/constants';
import { useIsGuest } from '@/creditflowresearch/application/User/access';
import {
  BOND_SCREENER_VIEWS_LIMIT_MESSAGE,
} from '@/creditflowresearch/application/BondScreener/Sidebar/bondScreenerViewsConstants';
import { ViewActions } from '@/creditflowresearch/ui/components/bondscreener/ViewActions';

import { CorporateBondScreenerSidebarTags } from './Tags';
import { getAccessTooltip } from './getAccessTooltip';

const getEmptyMessage = (emptyFilters: Record<string, boolean>): string | null => {
  const labels = Object.entries(emptyFilters)
    .filter(([, isEmpty]) => isEmpty)
    .map(([key]) => REQUIRED_FILTER_LABELS[key])
    .filter(Boolean);

  if (labels.length === 0) {
    return null;
  }

  return `Select at least ${ labels.join(' and ') }.`;
};

export const CorporateBondScreenerSidebarFooter = () => {
  const {
    apply: applyFilters,
    hasValidationError,
    emptyRequiredFilters,
  } = useCorporateBondScreenerSidebarFilterContext();
  const { applyColumns } = useColumnsCustomizationContext();
  const {
    views, activeViewId, commitActiveView, saveActiveView, saveAsNewView, canSaveActiveView, canCreateView,
  } = useBondScreenerViewsContext();
  // TODO: remove when the European market opening period ends
  const isRestricted = useIsGuest();

  const emptyMessage = getEmptyMessage(emptyRequiredFilters);
  const isDisabled = hasValidationError || !!emptyMessage;

  const canSaveActiveViewResolved = canSaveActiveView && !isRestricted;
  const canCreateViewResolved = canCreateView && !isRestricted;

  const applyAndSaveViewTooltip = getAccessTooltip(isRestricted, canSaveActiveView, 'Select a view to update it');
  const applyAndSaveNewViewTooltip = getAccessTooltip(isRestricted, canCreateView, BOND_SCREENER_VIEWS_LIMIT_MESSAGE);

  const handleApply = useCallback((): void => {
    applyFilters();
    applyColumns();
    commitActiveView(activeViewId);
  }, [applyFilters, applyColumns, commitActiveView, activeViewId]);

  const handleApplyAndSaveView = useCallback((): void => {
    handleApply();
    saveActiveView();
  }, [handleApply, saveActiveView]);

  const handleApplyAndSaveAsNewView = useCallback((name: string): void => {
    handleApply();
    saveAsNewView(name);
  }, [handleApply, saveAsNewView]);

  return (
    <Flex direction="column" gap={ 12 }>
      <CorporateBondScreenerSidebarTags />
      <Flex gap={ 10 } align="center" justify="space-between">
        <Tooltip content={ emptyMessage ?? '' } disabled={ !emptyMessage }>
          <BondScreenerApplySaveButton
            dataTest="applyFiltersButton"
            disabled={ isDisabled }
            canSaveActiveView={ canSaveActiveViewResolved }
            canCreateView={ canCreateViewResolved }
            existingNames={ views.map((view) => view.name) }
            applyAndSaveViewTooltip={ applyAndSaveViewTooltip }
            applyAndSaveNewViewTooltip={ applyAndSaveNewViewTooltip }
            onApplyOnly={ handleApply }
            onApplyAndSaveView={ handleApplyAndSaveView }
            onApplyAndSaveAsNewView={ handleApplyAndSaveAsNewView }
          />
        </Tooltip>
        <ViewActions resetDataTest="resetFiltersButton" isRestricted={ isRestricted } />
      </Flex>
    </Flex>
  );
};
